import type { JSX } from "react";
import { useEffect, useState } from "react";
import type { HealthResponse } from "@shared/types";
import { WhatsNew } from "../../whatsnew/WhatsNew";
import { ModelSelector } from "./ModelSelector";
import { loomApi } from "../../lib/loomApi";

interface TopbarProps {
  health: HealthResponse | null;
  loaded: boolean;
  downReason: string | null;
  /** Current chat title shown on the left of the bar. */
  title: string;
  /** M2 assistant pill: the serving assistant's name (server-rendered);
   *  absent when the agent-profile capability is off — nothing renders. */
  assistantName?: string;
  /** Pill click-through: opens the capability card (§09 tooltip copy). */
  onAssistantClick?: () => void;
  /** 换回 affordance after a transfer (§09: inline secondary action). */
  returnToName?: string;
  onReturnTo?: () => void;
  /** Toggle the off-canvas sidebar (mobile only; button hidden on desktop). */
  onToggleNav?: () => void;
  /** Open the 织灵账号 connection panel (the status icon clicks through to it). */
  onOpenLoom: () => void;
}

const MODE_KEY = "cyclecare_mode";

/**
 * Ambient 织灵 connection status: a small icon, green when connected / grey when
 * not. Click opens the connection panel. Refetches on a `loom-conn-changed` event
 * the panel fires after connect/disconnect, so the colour stays live.
 */
function LoomStatus({ onOpenLoom }: { onOpenLoom: () => void }): JSX.Element {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    let on = true;
    const refresh = (): void => {
      loomApi
        .status()
        .then((s) => {
          if (on) setConnected(s.connected);
        })
        .catch(() => {
          /* not logged in / offline — stay grey */
        });
    };
    refresh();
    window.addEventListener("loom-conn-changed", refresh);
    return () => {
      on = false;
      window.removeEventListener("loom-conn-changed", refresh);
    };
  }, []);
  return (
    <button
      className="icon-btn has-tip"
      type="button"
      onClick={onOpenLoom}
      aria-label="织灵账号"
      data-tip={connected ? "织灵 · 已连接" : "织灵 · 未连接"}
      style={{ color: connected ? "var(--color-success)" : "var(--text-tertiary)" }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 0 1 0 10h-2M8 12h8" />
      </svg>
    </button>
  );
}

/** Light/dark toggle for the B2B theme (data-mode on <html>, persisted). */
function ThemeToggle(): JSX.Element {
  const [mode, setMode] = useState<string>(
    () => document.documentElement.getAttribute("data-mode") ?? "light"
  );
  const toggle = (): void => {
    const next = mode === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-mode", next);
    localStorage.setItem(MODE_KEY, next);
    setMode(next);
  };
  return (
    <button
      className="icon-btn has-tip"
      type="button"
      onClick={toggle}
      aria-label="切换明暗模式"
      data-tip={mode === "dark" ? "切换到浅色" : "切换到深色"}
    >
      {mode === "dark" ? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      )}
    </button>
  );
}

export function Topbar({
  health,
  loaded,
  downReason,
  title,
  onToggleNav,
  onOpenLoom,
  assistantName,
  onAssistantClick,
  returnToName,
  onReturnTo
}: TopbarProps): JSX.Element {
  // The status pill doubles as the model picker (merged into one control): when
  // the backend is healthy it reads "● Cloud dashscope/<selector> · 健康", with
  // the inline ModelSelector standing in for the model name. Otherwise it shows a
  // plain connection-state label and no picker.
  let pillKind: "ok" | "warn" | "err" = "ok";
  let label: string | null = null;
  let healthy: { where: string; provider: string; status: string } | null = null;
  if (!loaded) {
    pillKind = "warn";
    label = "连接中…";
  } else if (downReason !== null) {
    pillKind = "err";
    label = `API 不可达 · ${downReason}`;
  } else if (health !== null) {
    pillKind = health.status === "ok" ? "ok" : "warn";
    healthy = {
      where: health.mode === "LOCAL" ? "Local" : "Cloud",
      provider: health.provider,
      status: health.status === "ok" ? "健康" : health.status
    };
  } else {
    pillKind = "err";
    label = "未知状态";
  }
  // The 织灵账号 connect entry exists solely to log into 织灵 to RUN there —
  // i.e. loom-live (FEATURE_IA_LOOM_LIVE). The other loom features do NOT use the
  // connection: loom-guide just emits deep links (the browser's own 织灵 session
  // handles them), media just shows videos/stepshows, and replay views committed
  // fixtures (its「试跑」is itself loom-live-gated). So gate the connect entry on
  // loom-live alone — otherwise it leaks a dead "connect" affordance under R2.
  const loomConnectVisible = health?.features?.loomLive === true;
  return (
    <header className="topbar">
      <button
        className="nav-toggle icon-btn"
        type="button"
        onClick={onToggleNav}
        aria-label="打开菜单"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <div className="topbar-title">
        {title}
        {assistantName !== undefined && assistantName.length > 0 && (
          <button
            className="assistant-pill has-tip"
            data-testid="assistant-pill"
            data-tip="当前助手 · 点开看它能做什么"
            onClick={onAssistantClick}
          >
            {assistantName}
          </button>
        )}
        {returnToName !== undefined && (
          <button className="assistant-return" data-testid="assistant-return" onClick={onReturnTo}>
            换回{returnToName}
          </button>
        )}
      </div>
      <div className="topbar-right">
        <span className={`status-pill status-pill-${pillKind}`}>
          <span className="dot" />
          {healthy !== null ? (
            <>
              {healthy.where} {healthy.provider}/
              <ModelSelector
                enabled={health?.features?.modelSelector === true}
                defaultModelName={health?.llmModelName}
              />
              {` · ${healthy.status}`}
            </>
          ) : (
            label
          )}
        </span>
        {loomConnectVisible && <LoomStatus onOpenLoom={onOpenLoom} />}
        <WhatsNew features={health?.features ?? {}} />
        <ThemeToggle />
      </div>
    </header>
  );
}
